import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { TurnSlots } from "../thread/slots";

/** `attachment`, in the terminal register — a glyph, a label, and a dim
 * trailing fact. */
@component({ name: "std-term-turn-attachment" })
export class TermTurnAttachment extends LonaWebComponent {
  private $glyph: HTMLElement = this.$("glyph");
  private $label: HTMLElement = this.$("label");
  private $meta: HTMLElement = this.$("meta");
  private bound = false;

  static readonly GLYPH: Record<TurnSlots.AttachmentKind, string> = {
    file: "▤",
    image: "◑",
    link: "↗",
    artifact: "◆",
  };

  static makeWith(props: TurnSlots.Props["attachment"]): TermTurnAttachment {
    return (TermTurnAttachment.make() as TermTurnAttachment).bind(props);
  }

  bind(props: TurnSlots.Props["attachment"]): this {
    this.$glyph.textContent = TermTurnAttachment.GLYPH[props.kind ?? "file"];
    this.$label.textContent = props.label;
    const meta = props.meta ?? "";
    this.$meta.textContent = meta;
    this.$meta.style.display = meta === "" ? "none" : "";
    const open = props.onOpen;
    this.classList.toggle("openable", open !== undefined);
    if (!this.bound) {
      this.bound = true;
      // Read the CURRENT props at click time rather than closing over this
      // bind's `open`: a pooled element is re-bound, and a stale closure
      // would open the previous row's attachment. Through the gesture
      // manager, so a drag that ends here does not count as opening.
      GESTURE_MANAGER.addPointerEvent(this, {
        onClick: () => this.onOpen?.(),
      });
    }
    this.onOpen = open;
    return this;
  }

  private onOpen: Option<() => void> = null;

  static $styles = [
    css`
      :host {
        display: flex;
        align-items: baseline;
        gap: 1ch;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--turn-term-size, 12px);
        line-height: var(--turn-term-leading, 1.7);
        color: var(--text-pri, #1f2328);
      }
      :host(.openable) {
        cursor: pointer;
      }
      :host(.openable):hover #label {
        text-decoration: underline;
      }
      #glyph {
        flex: none;
        width: 1ch;
        text-align: center;
        color: var(--text-ter, #8b93a7);
      }
      #meta {
        color: var(--text-ter, #8b93a7);
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <span id="glyph"></span>
    <span id="label"></span>
    <span id="meta"></span>
  `;
}
